import React, { useCallback, useEffect, useMemo, useState } from "react";
import Sidebar from "../Sidebar/Sidebar";
import {
  Alert,
  Autocomplete,
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid, GridActionsCellItem, GridToolbarContainer } from "@mui/x-data-grid";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import PlaylistAddCheckRoundedIcon from "@mui/icons-material/PlaylistAddCheckRounded";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { Redirect } from "react-router-dom";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import LocalstorageHelper from "../../LocalStorageHelper/LocalstorageHelper";

toast.configure();

const filterOptions = createFilterOptions({
  limit: 20,
  trim: true,
});

const compactInputSx = {
  "& .MuiInputBase-root": {
    minHeight: 31,
    fontSize: 12,
  },
  "& .MuiOutlinedInput-input": {
    padding: "7px 10px",
    fontSize: 12,
  },
  "& .MuiInputLabel-root": {
    fontSize: 12,
  },
};

const compactAutoSx = {
  "& .MuiInputBase-root": {
    minHeight: 31,
    fontSize: 12,
    pr: "34px !important",
  },
  "& .MuiOutlinedInput-input": {
    padding: "7px 10px !important",
    fontSize: 12,
  },
  "& .MuiInputLabel-root": {
    fontSize: 12,
  },
};

function getApiBase() {
  return process.env.REACT_APP_API_URL || "";
}

function buildApiUrl(apiBase, path, params) {
  const url = `${apiBase}${path}`;
  if (!params) return url;

  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      searchParams.append(key, String(value));
    }
  });

  const queryString = searchParams.toString();
  return queryString ? `${url}?${queryString}` : url;
}

function getAuthHeaders(userToken, withJson = false) {
  return {
    ...(withJson ? { "Content-Type": "application/json" } : {}),
    sptoken: userToken,
  };
}

async function parseResponse(response) {
  const text = await response.text();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch (err) {
    return text;
  }
}

async function requestJson(url, options) {
  const response = await fetch(url, options);
  const data = await parseResponse(response);

  if (!response.ok) {
    throw new Error(
      typeof data === "string" ? data : data?.message || "Request failed",
    );
  }

  return data;
}

function formatDateForInput(value) {
  if (!value) return dayjs().format("YYYY-MM-DD");
  const d = dayjs(value);
  return d.isValid() ? d.format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD");
}

function normalizeText(value) {
  return String(value || "").trim();
}

function getPartyLabel(party) {
  return party?.partydetails2 || party?.partydetails || "";
}

function getItemLabel(item) {
  if (!item) return "";
  return item.partdetails || `${item.itemcode || ""} - ${item.itemname || ""}`;
}

function getDocLabel(doc) {
  if (!doc?.documentno) return "";
  const dateText = dayjs(doc.challandate).isValid()
    ? ` - ${dayjs(doc.challandate).format("DD-MM-YYYY")}`
    : "";
  const waybillText = normalizeText(doc.ewbno) ? " - Waybill" : "";
  return `${doc.documentno}${dateText}${waybillText}`;
}

function getSourceRowId(row) {
  return `${row.warrantyclaimdtlid || ""}_${row.warrantyissuedtlid || ""}`;
}

function GridTopBar({ rowCount, title }) {
  return (
    <GridToolbarContainer>
      <Box
        sx={{
          px: 1,
          py: 0.5,
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: "#f7fbff",
          borderBottom: "1px solid #dbe7f3",
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: 11.5 }}>
          {title}
        </Typography>
        <Chip
          size="small"
          label={`Rows: ${rowCount}`}
          color="primary"
          variant="outlined"
          sx={{ height: 22, fontSize: 10.5 }}
        />
      </Box>
    </GridToolbarContainer>
  );
}

export default function WarrantyChallan() {
  const userToken = LocalstorageHelper.GetUserToken();
  const apiBase = getApiBase();

  const [mode, setMode] = useState(1);
  const [challanNo, setChallanNo] = useState("");
  const [challanDate, setChallanDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [remarks, setRemarks] = useState("");
  const [readonly, setReadonly] = useState(false);
  const [loadedDocId, setLoadedDocId] = useState(null);
  const [pdfStream, setPdfStream] = useState("");

  const [creditorList, setCreditorList] = useState([]);
  const [creditorValue, setCreditorValue] = useState(null);
  const [docList, setDocList] = useState([]);
  const [docValue, setDocValue] = useState(null);
  const [itemList, setItemList] = useState([]);
  const [itemValue, setItemValue] = useState(null);
  const [sourceSearch, setSourceSearch] = useState("");
  const [sourceRows, setSourceRows] = useState([]);
  const [selectedSourceIds, setSelectedSourceIds] = useState([]);
  const [rows, setRows] = useState([]);
  const [transporterList, setTransporterList] = useState([]);
  const [waybillOpen, setWaybillOpen] = useState(false);
  const [waybillTransporter, setWaybillTransporter] = useState(null);
  const [waybillGstNo, setWaybillGstNo] = useState("");
  const [waybillVehicleNo, setWaybillVehicleNo] = useState("");
  const [waybillFromPin, setWaybillFromPin] = useState("");
  const [waybillToPin, setWaybillToPin] = useState("");
  const [waybillDistance, setWaybillDistance] = useState("");

  const [loadingInitial, setLoadingInitial] = useState(false);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  const [loadingSource, setLoadingSource] = useState(false);
  const [saving, setSaving] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [loadingWaybill, setLoadingWaybill] = useState(false);

  const busy =
    loadingInitial ||
    loadingDocs ||
    loadingItems ||
    loadingSource ||
    saving ||
    updating ||
    printing ||
    loadingWaybill;
  const isEditMode = mode === 2;

  const redirectToLoginPage = () => {
    if (!userToken) {
      return <Redirect exact to="/sparepartsLogin" />;
    }
    return null;
  };

  useEffect(() => {
    if (!userToken) return undefined;

    const timeout = setTimeout(() => {
      LocalstorageHelper.RemoveUserToken();
    }, 3600000);

    return () => clearTimeout(timeout);
  }, [userToken]);

  useEffect(() => {
    return () => {
      if (pdfStream) {
        URL.revokeObjectURL(pdfStream);
      }
    };
  }, [pdfStream]);

  const renumberRows = useCallback((list) => {
    return list.map((row, idx) => ({
      ...row,
      srl: idx + 1,
    }));
  }, []);

  const resetForm = useCallback(() => {
    setChallanNo("");
    setChallanDate(dayjs().format("YYYY-MM-DD"));
    setRemarks("");
    setReadonly(false);
    setLoadedDocId(null);
    setCreditorValue(null);
    setDocValue(null);
    setItemValue(null);
    setSourceSearch("");
    setSourceRows([]);
    setSelectedSourceIds([]);
    setRows([]);
    setPdfStream("");
    setWaybillOpen(false);
    setWaybillTransporter(null);
    setWaybillGstNo("");
    setWaybillVehicleNo("");
    setWaybillFromPin("");
    setWaybillToPin("");
    setWaybillDistance("");
  }, []);

  const loadDocs = useCallback(async () => {
    if (!userToken) return;
    setLoadingDocs(true);

    try {
      const data = await requestJson(
        buildApiUrl(apiBase, "/warranty/challan/documentnos"),
        {
          method: "GET",
          headers: getAuthHeaders(userToken),
        },
      );

      setDocList(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to load challan documents");
    } finally {
      setLoadingDocs(false);
    }
  }, [apiBase, userToken]);

  const loadItems = useCallback(
    async (currentChallanId = loadedDocId) => {
      if (!userToken) return;
      setLoadingItems(true);

      try {
        const data = await requestJson(
          buildApiUrl(apiBase, "/warranty/challan/items", {
            currentchallanid: currentChallanId || "",
          }),
          {
            method: "GET",
            headers: getAuthHeaders(userToken),
          },
        );

        setItemList(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
        toast.error(err?.message || "Failed to load accepted items");
      } finally {
        setLoadingItems(false);
      }
    },
    [apiBase, loadedDocId, userToken],
  );

  const loadInitialData = useCallback(async () => {
    if (!userToken) return;
    setLoadingInitial(true);

    try {
      const [creditors, docs, items, transporters] = await Promise.allSettled([
        requestJson(buildApiUrl(apiBase, "/warranty/challan/creditors"), {
          method: "GET",
          headers: getAuthHeaders(userToken),
        }),
        requestJson(buildApiUrl(apiBase, "/warranty/challan/documentnos"), {
          method: "GET",
          headers: getAuthHeaders(userToken),
        }),
        requestJson(buildApiUrl(apiBase, "/warranty/challan/items"), {
          method: "GET",
          headers: getAuthHeaders(userToken),
        }),
        requestJson(buildApiUrl(apiBase, "/ewaybill/transdetails"), {
          method: "GET",
          headers: getAuthHeaders(userToken),
        }),
      ]);

      if (creditors.status === "fulfilled") {
        setCreditorList(Array.isArray(creditors.value) ? creditors.value : []);
      } else {
        console.error(creditors.reason);
        toast.error(creditors.reason?.message || "Failed to load creditors");
      }

      if (docs.status === "fulfilled") {
        setDocList(Array.isArray(docs.value) ? docs.value : []);
      } else {
        console.error(docs.reason);
        toast.error(docs.reason?.message || "Failed to load challan documents");
      }

      if (items.status === "fulfilled") {
        setItemList(Array.isArray(items.value) ? items.value : []);
      } else {
        console.error(items.reason);
        toast.error(items.reason?.message || "Failed to load accepted items");
      }

      if (transporters.status === "fulfilled") {
        setTransporterList(
          Array.isArray(transporters.value) ? transporters.value : [],
        );
      } else {
        console.error(transporters.reason);
        toast.error(
          transporters.reason?.message || "Failed to load transporters",
        );
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to load challan data");
    } finally {
      setLoadingInitial(false);
    }
  }, [apiBase, userToken]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const loadAcceptedRows = useCallback(async () => {
    if (!itemValue?.itemid) {
      toast.error("Select claimed battery item first");
      return;
    }

    setLoadingSource(true);

    try {
      const data = await requestJson(
        buildApiUrl(apiBase, "/warranty/challan/accepted", {
          itemid: itemValue.itemid,
          search: sourceSearch,
          currentchallanid: loadedDocId || "",
        }),
        {
          method: "GET",
          headers: getAuthHeaders(userToken),
        },
      );

      if (data?.type === "error") {
        toast.error(data.message || "No rows found");
        setSourceRows([]);
        return;
      }

      const list = Array.isArray(data?.rows) ? data.rows : [];
      setSourceRows(
        list.map((row) => ({
          ...row,
          id: getSourceRowId(row),
        })),
      );
      setSelectedSourceIds([]);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to load accepted serials");
    } finally {
      setLoadingSource(false);
    }
  }, [apiBase, itemValue, loadedDocId, sourceSearch, userToken]);

  const makeGridRow = useCallback((row, nextIndex) => {
    return {
      id: getSourceRowId(row),
      srl: nextIndex,
      warrantyclaimid: row.warrantyclaimid || null,
      warrantyclaimdtlid: row.warrantyclaimdtlid || null,
      warrantyissueid: row.warrantyissueid || null,
      warrantyissuedtlid: row.warrantyissuedtlid || null,
      warrantyreqid: row.warrantyreqid || null,
      warrantyreqdtlid: row.warrantyreqdtlid || null,
      claimno: row.claimno || "",
      claimsrl: row.claimsrl || null,
      issueno: row.issueno || "",
      issuesrl: row.issuesrl || null,
      reqdocno: row.reqdocno || "",
      reqsrl: row.reqsrl || null,
      ticketno: row.ticketno || "",
      customerpartyid: row.customerpartyid || null,
      customerpartyname: row.customerpartyname || "",
      claimeditemid: row.claimeditemid || null,
      claimeditemcode: row.claimeditemcode || "",
      claimeditemname: row.claimeditemname || "",
      claimeditemsrlno: row.claimeditemsrlno || "",
      issueditemid: row.issueditemid || null,
      issueditemcode: row.issueditemcode || "",
      issueditemname: row.issueditemname || "",
      issuedsrlno: row.issuedsrlno || "",
      remarks: row.remarks || "",
    };
  }, []);

  const addSelectedRows = useCallback(() => {
    if (readonly) return;

    const selectedSet = new Set(selectedSourceIds.map(String));
    const selectedRows = sourceRows.filter((row) => selectedSet.has(String(row.id)));

    if (!selectedRows.length) {
      toast.error("Select at least one accepted serial");
      return;
    }

    setRows((prevRows) => {
      const existing = new Set(prevRows.map((row) => String(row.id)));
      const additions = selectedRows
        .filter((row) => !existing.has(String(row.id)))
        .map((row, idx) => makeGridRow(row, prevRows.length + idx + 1));

      if (!additions.length) {
        toast.info("Selected serials are already in the grid");
        return prevRows;
      }

      return renumberRows([...prevRows, ...additions]);
    });

    setSelectedSourceIds([]);
  }, [makeGridRow, readonly, renumberRows, selectedSourceIds, sourceRows]);

  const deleteRow = useCallback(
    (id) => {
      if (readonly) return;
      setRows((prevRows) => renumberRows(prevRows.filter((row) => row.id !== id)));
    },
    [readonly, renumberRows],
  );

  const processRowUpdate = useCallback((newRow) => {
    const cleanRow = {
      ...newRow,
      remarks: normalizeText(newRow.remarks),
    };

    setRows((prevRows) =>
      prevRows.map((row) => (row.id === cleanRow.id ? cleanRow : row)),
    );

    return cleanRow;
  }, []);

  const buildPayload = useCallback(() => {
    return {
      challandate: challanDate,
      creditorid: creditorValue?.subledgerid || null,
      challanremarks: remarks,
      rows: renumberRows(rows).map((row) => ({
        srl: row.srl,
        warrantyclaimdtlid: row.warrantyclaimdtlid,
        warrantyissuedtlid: row.warrantyissuedtlid,
        claimeditemid: row.claimeditemid,
        remarks: row.remarks || "",
      })),
    };
  }, [challanDate, creditorValue, remarks, renumberRows, rows]);

  const validateBeforeSave = useCallback(() => {
    if (!challanDate) {
      toast.error("Challan date is required");
      return false;
    }

    if (!creditorValue?.subledgerid) {
      toast.error("Select sundry creditor");
      return false;
    }

    if (!rows.length) {
      toast.error("Add at least one serial in grid");
      return false;
    }

    return true;
  }, [challanDate, creditorValue, rows.length]);

  const loadChallanDocument = useCallback(async () => {
    if (!docValue?.headerid) {
      toast.error("Select challan document");
      return;
    }

    setLoadingDocs(true);

    try {
      const data = await requestJson(
        buildApiUrl(apiBase, `/warranty/challan/data/${docValue.headerid}`),
        {
          method: "GET",
          headers: getAuthHeaders(userToken),
        },
      );

      if (data?.type === "error") {
        toast.error(data.message || "Document not found");
        return;
      }

      const header = data.headerdata || {};
      const details = Array.isArray(data.detaildata) ? data.detaildata : [];
      setLoadedDocId(header.headerid || docValue.headerid);
      setChallanNo(header.challanno || "");
      setChallanDate(formatDateForInput(header.challandate));
      setRemarks(header.challanremarks || "");
      setReadonly(header.readonly === true);
      setWaybillGstNo(header.transgst || "");
      setWaybillVehicleNo(header.vehicleno || "");
      setWaybillDistance(header.distance || "");
      setWaybillTransporter(
        header.transname
          ? {
              transporterid: null,
              transportername: header.transname,
              gstno: header.transgst || "",
            }
          : null,
      );
      setCreditorValue({
        subledgerid: header.creditorid,
        partydetails: header.creditorpartydetails || "",
        partydetails2: header.creditorpartydetails2 || "",
      });
      setRows(
        renumberRows(
          details.map((row, idx) => ({
            ...row,
            id: getSourceRowId(row) || `LOADED_${idx + 1}`,
          })),
        ),
      );
      setSourceRows([]);
      setSelectedSourceIds([]);
      setItemValue(null);
      await loadItems(header.headerid || docValue.headerid);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to load challan document");
    } finally {
      setLoadingDocs(false);
    }
  }, [apiBase, docValue, loadItems, renumberRows, userToken]);

  const handleModeChange = useCallback(
    (event) => {
      const nextMode = Number(event.target.value);
      setMode(nextMode);
      resetForm();
      loadItems(null);
      if (nextMode === 2) {
        loadDocs();
      }
    },
    [loadDocs, loadItems, resetForm],
  );

  const handleSave = useCallback(async () => {
    if (!validateBeforeSave()) return;

    setSaving(true);

    try {
      const data = await requestJson(
        buildApiUrl(apiBase, "/warranty/challan/insert"),
        {
          method: "POST",
          headers: getAuthHeaders(userToken, true),
          body: JSON.stringify(buildPayload()),
        },
      );

      if (data?.type === "success") {
        toast.success(data.message || "Challan saved");
        setChallanNo(data.docno || "");
        setLoadedDocId(data.headerid || null);
        setMode(2);
        await loadDocs();
        await loadItems(data.headerid || null);
        return;
      }

      toast.error(data?.message || "Save failed");
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }, [apiBase, buildPayload, loadDocs, loadItems, userToken, validateBeforeSave]);

  const handleUpdate = useCallback(async () => {
    if (readonly) {
      toast.error("Waybill already generated. Challan is view-only.");
      return;
    }

    if (!loadedDocId) {
      toast.error("Load a challan document first");
      return;
    }

    if (!validateBeforeSave()) return;

    setUpdating(true);

    try {
      const data = await requestJson(
        buildApiUrl(apiBase, `/warranty/challan/update/${loadedDocId}`),
        {
          method: "PUT",
          headers: getAuthHeaders(userToken, true),
          body: JSON.stringify(buildPayload()),
        },
      );

      if (data?.type === "success") {
        toast.success(data.message || "Challan updated");
        await loadDocs();
        await loadItems(loadedDocId);
        return;
      }

      toast.error(data?.message || "Update failed");
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Update failed");
    } finally {
      setUpdating(false);
    }
  }, [
    apiBase,
    buildPayload,
    loadDocs,
    loadItems,
    loadedDocId,
    readonly,
    userToken,
    validateBeforeSave,
  ]);

  const handlePrint = useCallback(async () => {
    if (!loadedDocId) {
      toast.error("Save or load a challan first");
      return;
    }

    setPrinting(true);

    try {
      const response = await fetch(
        buildApiUrl(apiBase, `/reports/warranty/challan/${loadedDocId}`),
        {
          method: "POST",
          headers: getAuthHeaders(userToken),
        },
      );

      if (!response.ok) {
        const data = await parseResponse(response);
        throw new Error(
          typeof data === "string" ? data : data?.message || "Print failed",
        );
      }

      const blob = await response.blob();
      const pdfUrl = URL.createObjectURL(blob);
      setPdfStream((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return pdfUrl;
      });
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Print failed");
    } finally {
      setPrinting(false);
    }
  }, [apiBase, loadedDocId, userToken]);

  const openWaybillDialog = useCallback(async () => {
    if (!loadedDocId) {
      toast.error("Save or load a challan first");
      return;
    }

    setLoadingWaybill(true);

    try {
      const [details, transporters] = await Promise.all([
        requestJson(
          buildApiUrl(apiBase, `/warranty/challan/eway/details/${loadedDocId}`),
          {
            method: "GET",
            headers: getAuthHeaders(userToken),
          },
        ),
        transporterList.length
          ? Promise.resolve(transporterList)
          : requestJson(buildApiUrl(apiBase, "/ewaybill/transdetails"), {
              method: "GET",
              headers: getAuthHeaders(userToken),
            }),
      ]);

      if (details?.type === "error") {
        toast.error(details.message || "Failed to load waybill details");
        return;
      }

      const detail = details?.data || {};
      let nextTransporters = Array.isArray(transporters) ? transporters : [];

      let defaultTransporter = nextTransporters.find(
        (item) => String(item.transporterid) === String(detail.transporterid),
      );

      if (!defaultTransporter && detail.transportername) {
        defaultTransporter = {
          transporterid: detail.transporterid || null,
          transportername: detail.transportername,
          gstno: detail.gstno || "",
        };
        nextTransporters = [defaultTransporter, ...nextTransporters];
      }

      setTransporterList(nextTransporters);

      setWaybillTransporter(defaultTransporter);
      setWaybillGstNo(detail.transgst || defaultTransporter?.gstno || detail.gstno || "");
      setWaybillVehicleNo(detail.vehicleno || "");
      setWaybillFromPin(detail.frompincode || "");
      setWaybillToPin(detail.topincode || "");
      setWaybillDistance(detail.distance || "");
      setWaybillOpen(true);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to load waybill details");
    } finally {
      setLoadingWaybill(false);
    }
  }, [apiBase, loadedDocId, transporterList, userToken]);

  useEffect(() => {
    if (!waybillOpen || !waybillFromPin || !waybillToPin) return undefined;

    let cancelled = false;

    async function fetchDistance() {
      setLoadingWaybill(true);

      try {
        const data = await requestJson(buildApiUrl(apiBase, "/ewaybill/getdistance"), {
          method: "POST",
          headers: getAuthHeaders(userToken, true),
          body: JSON.stringify({
            frompin: waybillFromPin,
            topin: waybillToPin,
          }),
        });

        if (!cancelled && String(data?.IsSuccess).toLowerCase() === "true") {
          setWaybillDistance(data.Distance || "");
        }
      } catch (err) {
        console.error(err);
      } finally {
        if (!cancelled) {
          setLoadingWaybill(false);
        }
      }
    }

    fetchDistance();

    return () => {
      cancelled = true;
    };
  }, [apiBase, userToken, waybillFromPin, waybillOpen, waybillToPin]);

  const handleGenerateWaybill = useCallback(async () => {
    if (!loadedDocId) {
      toast.error("Save or load a challan first");
      return;
    }

    if (!normalizeText(waybillVehicleNo)) {
      toast.error("Vehicle number is required");
      return;
    }

    if (!normalizeText(waybillDistance)) {
      toast.error("Distance is required");
      return;
    }

    setLoadingWaybill(true);

    try {
      const data = await requestJson(
        buildApiUrl(apiBase, "/warranty/challan/eway/generate"),
        {
          method: "POST",
          headers: getAuthHeaders(userToken, true),
          body: JSON.stringify({
            transgst: waybillGstNo,
            transname: waybillTransporter?.transportername || "",
            vehicleno: waybillVehicleNo,
            distance: waybillDistance,
            id: loadedDocId,
          }),
        },
      );

      if (data?.type === "error") {
        toast.error(data.message || "Waybill generation failed");
        return;
      }

      if (String(data?.IsSuccess).toLowerCase() === "false") {
        toast.error(
          `${data.ErrorCode || ""}${data.ErrorCode ? ": " : ""}${
            data.ErrorMessage || "Waybill generation failed"
          }`,
          { autoClose: false },
        );
        return;
      }

      toast.success(
        `Ewaybill No ${data.EWayBill} generated against document ${data.DocNo}!`,
        { autoClose: false },
      );
      setReadonly(true);
      setWaybillOpen(false);
      await loadDocs();
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Waybill generation failed");
    } finally {
      setLoadingWaybill(false);
    }
  }, [
    apiBase,
    loadDocs,
    loadedDocId,
    userToken,
    waybillDistance,
    waybillGstNo,
    waybillTransporter,
    waybillVehicleNo,
  ]);

  const sourceColumns = useMemo(
    () => [
      {
        field: "claimno",
        headerName: "Claim No",
        minWidth: 115,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "claimdate",
        headerName: "Claim Date",
        minWidth: 100,
        headerClassName: "super-app-theme--header",
        valueFormatter: (params) =>
          params.value && dayjs(params.value).isValid()
            ? dayjs(params.value).format("DD-MM-YYYY")
            : "",
      },
      {
        field: "ticketno",
        headerName: "Ticket No",
        minWidth: 120,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "claimeditemsrlno",
        headerName: "Claimed Serial",
        minWidth: 145,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "issuedsrlno",
        headerName: "Issued Serial",
        minWidth: 145,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "issueditem",
        headerName: "Issued Item",
        minWidth: 260,
        flex: 1,
        headerClassName: "super-app-theme--header",
        valueGetter: (params) =>
          `${params.row.issueditemcode || ""} - ${params.row.issueditemname || ""}`,
      },
      {
        field: "customerpartyname",
        headerName: "Customer Party",
        minWidth: 220,
        flex: 1,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "issueno",
        headerName: "Issue No",
        minWidth: 115,
        headerClassName: "super-app-theme--header",
      },
    ],
    [],
  );

  const gridColumns = useMemo(
    () => [
      {
        field: "srl",
        headerName: "#",
        width: 55,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "claimno",
        headerName: "Claim No",
        minWidth: 115,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "issueno",
        headerName: "Issue No",
        minWidth: 115,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "ticketno",
        headerName: "Ticket No",
        minWidth: 120,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "customerpartyname",
        headerName: "Customer Party",
        minWidth: 220,
        flex: 1,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "claimeditem",
        headerName: "Claimed Item",
        minWidth: 250,
        flex: 1,
        headerClassName: "super-app-theme--header",
        valueGetter: (params) =>
          `${params.row.claimeditemcode || ""} - ${params.row.claimeditemname || ""}`,
      },
      {
        field: "claimeditemsrlno",
        headerName: "Claimed Serial",
        minWidth: 145,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "issueditem",
        headerName: "Issued Item",
        minWidth: 250,
        flex: 1,
        headerClassName: "super-app-theme--header",
        valueGetter: (params) =>
          `${params.row.issueditemcode || ""} - ${params.row.issueditemname || ""}`,
      },
      {
        field: "issuedsrlno",
        headerName: "Issued Serial",
        minWidth: 145,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "remarks",
        headerName: "Remarks",
        minWidth: 180,
        editable: !readonly,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "actions",
        type: "actions",
        headerName: "",
        width: 55,
        getActions: (params) => [
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            disabled={readonly}
            onClick={() => deleteRow(params.row.id)}
          />,
        ],
      },
    ],
    [deleteRow, readonly],
  );

  return (
    <Box>
      <Sidebar
        Title={"WARRANTY CHALLAN"}
        expandedPanel={"panel2"}
        children={
          <Box
            component="form"
            sx={{
              width: "100%",
              "& .MuiCard-root": { borderRadius: 1.5 },
            }}
            autoComplete="off"
          >
            <Backdrop
              open={busy}
              sx={{
                color: "#fff",
                zIndex: (theme) => theme.zIndex.drawer + 999,
                backdropFilter: "blur(1.5px)",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <CircularProgress color="inherit" size={24} />
              <Typography sx={{ fontWeight: 700, fontSize: 12 }}>
                {printing
                  ? "Preparing print..."
                  : saving || updating
                    ? "Saving challan..."
                    : "Loading warranty challan..."}
              </Typography>
            </Backdrop>

            {readonly && (
              <Alert severity="warning" sx={{ mb: 1, py: 0.25, fontSize: 12 }}>
                Waybill is already generated for this challan. Editing is locked.
              </Alert>
            )}

            <Card elevation={1} sx={{ mb: 1 }}>
              <CardContent sx={{ p: "7px 8px !important" }}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={0.75}
                  alignItems={{ xs: "stretch", md: "center" }}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <TextField
                    select
                    label="Mode"
                    value={mode}
                    onChange={handleModeChange}
                    sx={{ width: 86, ...compactInputSx }}
                    size="small"
                  >
                    <MenuItem value={1}>New</MenuItem>
                    <MenuItem value={2}>Edit</MenuItem>
                  </TextField>

                  <TextField
                    disabled
                    label="Challan No"
                    value={challanNo}
                    sx={{ width: 130, ...compactInputSx }}
                    size="small"
                  />

                  <TextField
                    label="Challan Date"
                    type="date"
                    value={challanDate}
                    onChange={(e) => setChallanDate(e.target.value)}
                    disabled={readonly}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 135, ...compactInputSx }}
                    size="small"
                  />

                  <Autocomplete
                    disabled={readonly}
                    value={creditorValue}
                    onChange={(event, newValue) => setCreditorValue(newValue)}
                    options={creditorList}
                    loading={loadingInitial}
                    filterOptions={filterOptions}
                    getOptionLabel={getPartyLabel}
                    isOptionEqualToValue={(option, val) =>
                      option?.subledgerid === val?.subledgerid
                    }
                    sx={{ minWidth: 320, flex: 1.2, ...compactAutoSx }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="Sundry Creditor"
                        placeholder="Search creditor"
                      />
                    )}
                  />

                  <TextField
                    label="Remarks"
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    disabled={readonly}
                    size="small"
                    sx={{ minWidth: 220, flex: 1, ...compactInputSx }}
                  />

                  <Tooltip title="Generate e-way bill for this challan">
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        color="secondary"
                        disabled={!loadedDocId || readonly || loadingWaybill}
                        onClick={openWaybillDialog}
                        startIcon={<LocalShippingOutlinedIcon />}
                        sx={{ height: 31, fontSize: 12, whiteSpace: "nowrap" }}
                      >
                        Generate Waybill
                      </Button>
                    </span>
                  </Tooltip>

                  <Button
                    size="small"
                    variant="contained"
                    color="info"
                    disabled={!loadedDocId}
                    onClick={handlePrint}
                    startIcon={<PrintOutlinedIcon />}
                    sx={{ height: 31, fontSize: 12, whiteSpace: "nowrap" }}
                  >
                    Print
                  </Button>
                </Stack>

                {isEditMode && (
                  <>
                    <Divider sx={{ my: 0.8 }} />
                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      spacing={0.75}
                      alignItems={{ xs: "stretch", md: "center" }}
                    >
                      <Autocomplete
                        value={docValue}
                        onChange={(event, newValue) => setDocValue(newValue)}
                        options={docList}
                        loading={loadingDocs}
                        filterOptions={filterOptions}
                        getOptionLabel={getDocLabel}
                        isOptionEqualToValue={(option, val) =>
                          option?.headerid === val?.headerid
                        }
                        sx={{ minWidth: 320, flex: 1, ...compactAutoSx }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            label="Challan Document"
                            placeholder="Search challan"
                          />
                        )}
                      />

                      <Button
                        size="small"
                        variant="contained"
                        onClick={loadChallanDocument}
                        startIcon={<SearchRoundedIcon />}
                        sx={{ height: 31, fontSize: 12, minWidth: 88 }}
                      >
                        Search
                      </Button>

                      <Button
                        size="small"
                        variant="outlined"
                        onClick={loadDocs}
                        startIcon={<RefreshIcon />}
                        sx={{ height: 31, fontSize: 12, minWidth: 88 }}
                      >
                        Refresh
                      </Button>
                    </Stack>
                  </>
                )}
              </CardContent>
            </Card>

            <Card elevation={1} sx={{ mb: 1 }}>
              <CardContent sx={{ p: "7px 8px !important" }}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={0.75}
                  alignItems={{ xs: "stretch", md: "center" }}
                  useFlexGap
                  flexWrap="wrap"
                  sx={{ mb: 1 }}
                >
                  <Autocomplete
                    disabled={readonly}
                    value={itemValue}
                    onChange={(event, newValue) => {
                      setItemValue(newValue);
                      setSourceRows([]);
                      setSelectedSourceIds([]);
                    }}
                    options={itemList}
                    loading={loadingItems}
                    filterOptions={filterOptions}
                    getOptionLabel={getItemLabel}
                    isOptionEqualToValue={(option, val) =>
                      option?.itemid === val?.itemid
                    }
                    sx={{ minWidth: 360, flex: 1.2, ...compactAutoSx }}
                    renderOption={(props, option) => (
                      <li {...props} key={option.itemid}>
                        <Box>
                          <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
                            {option.itemcode} - {option.itemname}
                          </Typography>
                          <Typography sx={{ fontSize: 10.5, color: "text.secondary" }}>
                            Accepted pending rows: {option.pendingcount || 0}
                          </Typography>
                        </Box>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="Claimed Battery Item"
                        placeholder="Search claimed item"
                      />
                    )}
                  />

                  <TextField
                    disabled={readonly}
                    label="Search Serial / Claim / Party"
                    value={sourceSearch}
                    onChange={(e) => setSourceSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        loadAcceptedRows();
                      }
                    }}
                    size="small"
                    sx={{ minWidth: 240, flex: 1, ...compactInputSx }}
                  />

                  <Button
                    disabled={readonly}
                    variant="contained"
                    onClick={loadAcceptedRows}
                    startIcon={<SearchRoundedIcon />}
                    sx={{ height: 31, minWidth: 86, fontSize: 12 }}
                  >
                    Fetch
                  </Button>

                  <Button
                    disabled={readonly}
                    variant="contained"
                    color="success"
                    onClick={addSelectedRows}
                    startIcon={<PlaylistAddCheckRoundedIcon />}
                    sx={{ height: 31, minWidth: 120, fontSize: 12 }}
                  >
                    Add Selected
                  </Button>
                </Stack>

                <Box
                  sx={{
                    height: 255,
                    width: "100%",
                    "& .super-app-theme--header": {
                      backgroundColor: "#ebf5ff",
                      color: "#0d3557",
                      fontWeight: 800,
                      fontSize: 11.25,
                    },
                    "& .MuiDataGrid-cell": {
                      fontSize: 11.25,
                      py: 0,
                    },
                    "& .MuiDataGrid-footerContainer": {
                      minHeight: 34,
                    },
                  }}
                >
                  <DataGrid
                    rows={sourceRows}
                    columns={sourceColumns}
                    getRowId={(row) => row.id}
                    checkboxSelection={!readonly}
                    disableSelectionOnClick
                    selectionModel={selectedSourceIds}
                    onSelectionModelChange={(newSelectionModel) =>
                      setSelectedSourceIds(newSelectionModel)
                    }
                    density="compact"
                    rowHeight={28}
                    headerHeight={30}
                    components={{
                      Toolbar: () => (
                        <GridTopBar
                          rowCount={sourceRows.length}
                          title="Accepted claim serials not yet challaned"
                        />
                      ),
                    }}
                    rowsPerPageOptions={[5, 10, 25]}
                    initialState={{
                      pagination: { pageSize: 5 },
                    }}
                  />
                </Box>
              </CardContent>
            </Card>

            <Card elevation={1} sx={{ mb: 1 }}>
              <CardContent sx={{ p: 0 }}>
                <Box
                  sx={{
                    height: 370,
                    width: "100%",
                    "& .super-app-theme--header": {
                      backgroundColor: "#ebf5ff",
                      color: "#0d3557",
                      fontWeight: 800,
                      fontSize: 11.25,
                    },
                    "& .MuiDataGrid-cell": {
                      fontSize: 11.25,
                      py: 0,
                    },
                    "& .MuiDataGrid-footerContainer": {
                      minHeight: 36,
                    },
                  }}
                >
                  <DataGrid
                    rows={rows}
                    columns={gridColumns}
                    getRowId={(row) => row.id}
                    density="compact"
                    rowHeight={28}
                    headerHeight={30}
                    disableSelectionOnClick
                    editMode="cell"
                    experimentalFeatures={{ newEditingApi: true }}
                    processRowUpdate={processRowUpdate}
                    isCellEditable={(params) => !readonly && params.field === "remarks"}
                    onProcessRowUpdateError={(error) => {
                      console.error(error);
                      toast.error(error?.message || "Failed to update row");
                    }}
                    components={{
                      Toolbar: () => (
                        <GridTopBar
                          rowCount={rows.length}
                          title="Warranty challan rows"
                        />
                      ),
                    }}
                    rowsPerPageOptions={[10, 20, 50]}
                    initialState={{
                      pagination: { pageSize: 10 },
                    }}
                  />
                </Box>
              </CardContent>
            </Card>

            <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 1 }}>
              {mode === 1 && (
                <Button
                  disabled={saving || readonly}
                  onClick={handleSave}
                  size="small"
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  sx={{ borderRadius: 1.2, minWidth: 90, height: 31, fontSize: 12 }}
                >
                  Save
                </Button>
              )}

              {mode === 2 && (
                <Button
                  disabled={updating || readonly || !loadedDocId}
                  onClick={handleUpdate}
                  size="small"
                  variant="contained"
                  color="warning"
                  startIcon={<SaveIcon />}
                  sx={{ borderRadius: 1.2, minWidth: 90, height: 31, fontSize: 12 }}
                >
                  Update
                </Button>
              )}
            </Stack>

            <Dialog
              open={waybillOpen}
              onClose={() => {
                if (!loadingWaybill) setWaybillOpen(false);
              }}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle sx={{ fontSize: 15, fontWeight: 800 }}>
                Generate e-Way Bill
              </DialogTitle>
              <DialogContent dividers>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={1}
                  useFlexGap
                  flexWrap="wrap"
                >
                  <Autocomplete
                    value={waybillTransporter}
                    onChange={(event, newValue) => {
                      setWaybillTransporter(newValue);
                      setWaybillGstNo(newValue?.gstno || "");
                    }}
                    options={transporterList}
                    filterOptions={filterOptions}
                    getOptionLabel={(option) => option?.transportername || ""}
                    isOptionEqualToValue={(option, val) =>
                      option?.transporterid === val?.transporterid
                    }
                    sx={{ minWidth: 300, flex: 1, ...compactAutoSx }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="Transporter"
                        placeholder="Search transporter"
                      />
                    )}
                  />

                  <TextField
                    label="GST No"
                    value={waybillGstNo}
                    onChange={(e) => setWaybillGstNo(e.target.value)}
                    size="small"
                    sx={{ width: 190, ...compactInputSx }}
                  />

                  <TextField
                    label="Vehicle No"
                    value={waybillVehicleNo}
                    onChange={(e) => setWaybillVehicleNo(e.target.value)}
                    size="small"
                    sx={{ width: 150, ...compactInputSx }}
                  />

                  <TextField
                    label="From Pin"
                    type="number"
                    value={waybillFromPin}
                    onChange={(e) => setWaybillFromPin(e.target.value)}
                    size="small"
                    sx={{ width: 120, ...compactInputSx }}
                  />

                  <TextField
                    label="To Pin"
                    type="number"
                    value={waybillToPin}
                    onChange={(e) => setWaybillToPin(e.target.value)}
                    size="small"
                    sx={{ width: 120, ...compactInputSx }}
                  />

                  <TextField
                    label="Distance"
                    type="number"
                    value={waybillDistance}
                    onChange={(e) => setWaybillDistance(e.target.value)}
                    size="small"
                    sx={{ width: 120, ...compactInputSx }}
                  />
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button
                  size="small"
                  onClick={() => setWaybillOpen(false)}
                  disabled={loadingWaybill}
                >
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleGenerateWaybill}
                  disabled={loadingWaybill}
                  startIcon={<LocalShippingOutlinedIcon />}
                >
                  Generate
                </Button>
              </DialogActions>
            </Dialog>

            {pdfStream && (
              <Card elevation={1} sx={{ mb: 1 }}>
                <CardContent sx={{ p: "7px 8px !important" }}>
                  <Typography sx={{ mb: 0.75, fontWeight: 700, fontSize: 12 }}>
                    Warranty Challan Print Preview
                  </Typography>
                  <Box
                    component="iframe"
                    title="Warranty Challan Print"
                    src={pdfStream}
                    sx={{
                      width: "100%",
                      height: 520,
                      border: "1px solid #dbe7f3",
                      borderRadius: 1,
                    }}
                  />
                </CardContent>
              </Card>
            )}
          </Box>
        }
      />
      {redirectToLoginPage()}
    </Box>
  );
}
